import { motion } from "framer-motion";
import { useT } from "@/lib/i18n";
import ScreenHeading from "./ScreenHeading";
import { HEADING_GAP, LIST_GAP, ROW_BORDER, ROW_SHAPE, ROW_SURFACE } from "../layout";
import type { HowItWorksScreenDef, ScreenComponentProps } from "../types";

/**
 * The screen that answers "what am I actually buying?".
 *
 * The control flow never answered it before the payment sheet, which is where
 * 76% of everyone who got that far dropped. Five beats, in the order the
 * product happens, with the payload last: what you keep is people, not a meal.
 *
 * The group chat gets its own beat rather than a clause inside the matching
 * one. It is the single biggest answer to "I'd have to walk in cold", and it
 * is invisible until you have already paid, so nobody is weighing it at the
 * price. It is phrased as something the reader does, not something we provide,
 * because the fear being answered is social, and a feature does not calm that
 * the way picturing yourself already mid-conversation does. The auto-posted
 * icebreaker (server/src/constants/introMessages.ts) is what makes that
 * promise true: nobody has to be the one who speaks first.
 *
 * The closing line sells the novelty rather than the membership: a place you
 * would not have picked, a table you have not met. Said here, while nothing is
 * being sold, that reads as the appeal of the thing. The commercial version of
 * the same argument (it takes a few tables before someone sticks) waits for the
 * plans screen, where a price is on screen to justify.
 */
export default function HowItWorksScreen({
  def,
}: ScreenComponentProps<HowItWorksScreenDef>) {
  const { t } = useT();

  return (
    <div className="flex-1 flex flex-col">
      <ScreenHeading
        kicker={t(`${def.copyKey}.kicker`)}
        title={t(`${def.copyKey}.title`)}
      />

      <ol className={`${HEADING_GAP} flex flex-col ${LIST_GAP}`}>
        {def.beats.map((beat, i) => (
          <motion.li
            key={beat}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.06 * i, duration: 0.3 }}
            className={`flex gap-3.5 ${ROW_BORDER} ${ROW_SHAPE} ${ROW_SURFACE}`}
          >
            <span
              aria-hidden
              className="flex-none w-6 h-6 rounded-full bg-primary-500/15 text-primary-400 text-[12px] font-semibold flex items-center justify-center"
            >
              {i + 1}
            </span>
            <span className="flex-1 min-w-0">
              <span className="block font-medium text-[15px] leading-snug text-neutral-900 dark:text-neutral-50">
                {t(`${def.copyKey}.beats.${beat}.title`)}
              </span>
              <span className="block text-[13px] leading-relaxed text-neutral-500 dark:text-neutral-400 mt-1">
                {t(`${def.copyKey}.beats.${beat}.body`)}
              </span>
            </span>
          </motion.li>
        ))}
      </ol>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3, duration: 0.35 }}
        className="mt-5 mb-2 text-[13px] leading-relaxed text-neutral-500 dark:text-neutral-400"
      >
        {t(`${def.copyKey}.footnote`)}
      </motion.p>
    </div>
  );
}
